import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import { getUtmParamsForApi } from '../../utils/utmTracker';
import InlineCheckoutForm from '../../components/InlineCheckoutForm/InlineCheckoutForm';
import { buildSalesLeadCheckoutParams } from '../../utils/salesLeadCheckoutParams';
import useSalesPromptRouting from '../../hooks/useSalesPromptRouting';
import { buildAuthOverlayRouteState } from '../../utils/authOverlayNavigation';

// Import base styles and then our custom overrides
import './SalesPage.css'; 
import './SalesPage07.css';

export const SALES07_SIGNUP_VARIANTS = {
  control: {
    pageVariant: 'v07_assistant',
    framingVariant: 'free_trial',
    formVariant: 'full_form',
    designVariant: 'sales07',
    subtitleStrong: 'Start your free trial today.',
    contactButtonLabel: 'START MY FREE TRIAL ->',
    footer: 'No credit card required. Cancel anytime.',
    showNameField: true,
    showPhoneField: true,
  },
  full_form: {
    pageVariant: 'v07_create_account_full_form',
    framingVariant: 'create_account',
    formVariant: 'full_form',
    designVariant: 'sales07',
    subtitleStrong: 'Create your BotSpot account today.',
    contactButtonLabel: 'CREATE ACCOUNT ->',
    footer: 'Create your account, then choose how you want to start.',
    showNameField: true,
    showPhoneField: true,
  },
  email_only: {
    pageVariant: 'v07_create_account_email_only',
    framingVariant: 'create_account',
    formVariant: 'email_only',
    designVariant: 'sales07',
    subtitleStrong: 'Create your BotSpot account today.',
    contactButtonLabel: 'CREATE ACCOUNT ->',
    footer: 'Create your account, then choose how you want to start.',
    showNameField: false,
    showPhoneField: false,
  },
};

const getSales07VariantConfig = (signupVariant) => (
  SALES07_SIGNUP_VARIANTS[signupVariant] || SALES07_SIGNUP_VARIANTS.control
);

/**
 * SalesPage07 - The "Conversational Assistant" Variant
 * Focus: Ease of use, plain-English strategy building, futuristic assistant vibe.
 */
export default function SalesPage07({
  signupVariant = 'control',
  experimentContext = null,
  trackSignupFramingExperiment = true,
  onLoginClick = null,
} = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const variantConfig = getSales07VariantConfig(signupVariant);
  const {
    showLoginHint,
    loginReturnTo,
    getLeadSuccessPath,
  } = useSalesPromptRouting({ pageVariant: variantConfig.pageVariant });

  // Analytics & Experiment Context
  useEffect(() => {
    const featureFlagKey = experimentContext?.feature_flag || 'sales_page_signup_framing_v4_variant';
    const featureFlagProperty = `$feature_flag_${featureFlagKey}`;
    const signupFramingExperimentContext = trackSignupFramingExperiment ? {
      ...(experimentContext || {}),
      experiment_name: experimentContext?.experiment_name || 'sales_page_design_07_test',
      experiment_variant: experimentContext?.experiment_variant || signupVariant,
      feature_flag: featureFlagKey,
      [featureFlagProperty]: experimentContext?.[featureFlagProperty] || signupVariant,
    } : {
      experiment_variant: signupVariant,
    };
    const nextExperimentContext = {
      ...signupFramingExperimentContext,
      sales_page_signup_framing_variant: signupVariant,
      sales_page_form_variant: variantConfig.formVariant,
      sales_page_framing_variant: variantConfig.framingVariant,
      sales_page_design_variant: variantConfig.designVariant,
      $feature_flag_sales_page_design_variant: variantConfig.designVariant,
    };
    window.__experimentContext = nextExperimentContext;

    const utmData = getUtmParamsForApi();
    pushEvent({
      event: ANALYTICS_EVENTS.PAGE_VIEW,
      page: 'sales_v07_assistant',
      source: location.pathname,
      variant: signupVariant,
      ...nextExperimentContext,
      ...utmData
    });
  }, [experimentContext, location, signupVariant, trackSignupFramingExperiment, variantConfig.formVariant, variantConfig.framingVariant, variantConfig.designVariant]);

  const handleLoginClick = () => {
    const safeLoginReturnTo = loginReturnTo || '/';
    pushEvent({
      event: ANALYTICS_EVENTS.LOGIN_CLICKED,
      method: 'sales_v07_link',
      source: location.pathname,
      returnTo: safeLoginReturnTo,
      ...(experimentContext || {})
    });

    if (typeof onLoginClick === 'function') {
      onLoginClick();
      return;
    }

    navigate(`/login?returnTo=${encodeURIComponent(safeLoginReturnTo)}`, {
      state: buildAuthOverlayRouteState(location),
    });
  };

  const handleContactSuccess = ({ contactData, leadTransactionId, autoLoginToken, howFoundUs, howFoundUsOtherText, existingUser, redirectTo }) => {
    if (existingUser && redirectTo) {
      navigate(redirectTo);
      return;
    }

    const signupFramingExperimentExtra = trackSignupFramingExperiment ? {
      [`$feature/${experimentContext?.feature_flag || 'sales_page_signup_framing_v4_variant'}`]: experimentContext?.[`$feature/${experimentContext?.feature_flag || 'sales_page_signup_framing_v4_variant'}`],
      $feature_flag_sales_page_signup_framing_v4_variant: experimentContext?.$feature_flag_sales_page_signup_framing_v4_variant,
      feature_flag: experimentContext?.feature_flag,
      sales_page_signup_framing_v4_experiment_name: experimentContext?.experiment_name,
      sales_page_signup_framing_v4_variant: experimentContext?.$feature_flag_sales_page_signup_framing_v4_variant,
      sales_page_signup_framing_v4_route_variant: experimentContext?.sales_page_signup_framing_v4_route_variant,
    } : {};
    const params = buildSalesLeadCheckoutParams({
      contactData,
      leadTransactionId,
      variant: signupVariant,
      howFoundUs,
      howFoundUsOtherText,
      extra: {
        ...signupFramingExperimentExtra,
        sales_page_signup_framing_variant: signupVariant,
        sales_page_form_variant: variantConfig.formVariant,
        sales_page_framing_variant: variantConfig.framingVariant,
        sales_page_design_variant: variantConfig.designVariant,
      },
    });

    navigate(getLeadSuccessPath(params, autoLoginToken));
  };

  const checkoutForm = (
    <InlineCheckoutForm
      onContactSuccess={handleContactSuccess}
      contactButtonLabel={variantConfig.contactButtonLabel}
      showNameField={variantConfig.showNameField}
      showPhoneField={variantConfig.showPhoneField}
      emailPlaceholder="Email address"
    />
  );

  return (
    <>
      <Helmet>
        <title>BotSpot | Your Trading Ideas, Executed by AI</title>
        <meta name="description" content="Stop building logic gates. Start describing trades. BotSpot turns your plain-English strategies into live automated bots instantly." />
        <link rel="canonical" href="https://botspot.trade/sales" />
      </Helmet>

      <div className="sales-page-minimal v07-assistant-theme">
        {/* Animated Background */}
        <div className="hero-background">
          <div className="v07-pulse-glow"></div>
          <div className="v07-data-stream"></div>
          <div className="floating-shapes">
            <div className="shape v07-shape-primary"></div>
            <div className="shape v07-shape-accent"></div>
          </div>
        </div>

        <Container>
          <div className="content-wrapper v07-centered-layout">
            <div className="hero-content">
              
              <div className="v07-badge-wrapper">
                <span className="v07-status-dot"></span>
                <span className="v07-badge-text">AI Command Center Online</span>
              </div>

              <h1 className="hero-title v07-headline">
                Your Strategy. <br />
                <span className="v07-gradient-text">Decoded by AI.</span>
              </h1>

              <p className="hero-subtitle v07-sub-headline">
                Stop clicking through complex menus. Describe your trading idea in 
                <strong> plain English</strong> and let our AI handle the research, logic, code, and execution. <strong>{variantConfig.subtitleStrong}</strong>
              </p>

              <div className="v07-form-glass-card">
                {checkoutForm}
                <p className="v07-trust-footer">⚡ Idea to Execution in &lt; 60 Seconds</p>
                <p className="v07-no-cc-text">{variantConfig.footer}</p>
              </div>

              {showLoginHint && (
                <div className="v07-login-hint">
                  Already a member? <button onClick={handleLoginClick} className="v07-login-btn">Log in</button>
                </div>
              )}
            </div>
          </div>
        </Container>
      </div>
    </>
  );
}
